import { Skeleton, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { hexToRgba } from "../../../utils/colors.ts"

import { nearbyBreakdownRows } from "./nearby-breakdown-rows.tsx"
import {
  NEARBY_RADIUS_MI,
  type UseNearbyFeaturesResult
} from "./useNearbyFeatures.ts"

const { Text, Title } = Typography

interface NearbyBreakdownSummaryProps {
  status: UseNearbyFeaturesResult["status"]
  counts: UseNearbyFeaturesResult["counts"]
}

/**
 * The counts-only half of the Nearby tab (`QuickViewNearby`), rendered
 * persistently below the Overview fields and below the Files list — per
 * explicit product direction, this summary must stay visible whichever of
 * those two tabs is open, not only when the user has switched to the
 * dedicated Nearby tab (which keeps its own copy of this same breakdown,
 * plus the filterable row list this summary deliberately omits). Both read
 * from the one `useNearbyFeatures` call `EntityQuickView` makes, so this
 * never triggers a second fetch of its own.
 *
 * `mt-auto` pushes this to the bottom of its scroll container (which
 * `EntityQuickView` makes a flex column for exactly this) so it always
 * lands immediately above the Open Project/View on Map footer rather than
 * trailing right under a short Overview/Files body with a gap beneath it —
 * on a tall body that overflows the scroll area, `mt-auto` has no room to
 * act and this simply follows the content, still directly above the
 * footer. `border-t` is a plain 1px solid rule (Tailwind's default border
 * style/width — never bolded), matching the footer's own separator.
 */
const NearbyBreakdownSummary = ({
  status,
  counts
}: NearbyBreakdownSummaryProps) => {
  const { t } = useTranslation()

  if (status === "loading") {
    return (
      <div className="border-t border-slate-200 pt-4">
        <Skeleton active title={{ width: 80 }} paragraph={{ rows: 2 }} />
      </div>
    )
  }

  if (!counts) return null

  const totalCount =
    counts.project + counts.lead + counts.subcontractor + counts.appointment
  const breakdownRows = nearbyBreakdownRows(t)

  return (
    <div
      className="flex flex-col gap-2 border-t border-slate-200 pt-4"
      data-testid="quick-view-nearby-summary"
    >
      <Title
        level={5}
        className="!mb-0 !text-[15px] font-semibold !text-[#0F172A]"
      >
        {t("globalMap.quickView.tabs.nearby")}
      </Title>
      <Text className="text-xs text-slate-500">
        {t("globalMap.quickView.nearby.recordsWithinRadius", {
          count: totalCount,
          miles: NEARBY_RADIUS_MI
        })}
      </Text>
      <ul className="m-0 mt-1 flex list-none flex-col divide-y divide-slate-100 p-0">
        {breakdownRows.map((row) => (
          <li
            key={row.key}
            className="flex h-11 items-center justify-between gap-2 text-[13px]"
          >
            <span className="flex min-w-0 items-center gap-2.5 text-slate-700">
              {/* Same tinted-tile treatment as the KPI row and the quick
                  view's own header, at row scale. */}
              <span
                aria-hidden="true"
                className="flex size-7 shrink-0 items-center justify-center rounded-md text-[15px]"
                style={{
                  color: row.color,
                  backgroundColor: hexToRgba(row.color, 0.12)
                }}
              >
                {row.icon}
              </span>
              <span className="truncate">{row.label}</span>
            </span>
            <span className="shrink-0 font-semibold text-slate-900">
              {counts[row.key]}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export { NearbyBreakdownSummary }
